import {fromLonLat} from "ol/proj";
import {LineString, MultiPolygon, Point, Polygon} from "ol/geom";
import {Feature} from "ol";
import {GeoJSON} from "ol/format";
import * as turf from "@turf/turf";
import {nanoid} from "nanoid";
import * as polygonClipping from '../libs/polygon-clipping/polygon-clipping.umd'

export const createFeatureFromCoordinates = (coordinates, name = null, type = 'Polygon') => {
    let coordinatesArray = [];
    let geo;
    let points = coordinates.split(" ");

    //sprawdzenie czy to multipolygon
    if (coordinates.includes('|') || coordinates.includes("?")) {

        //tablica elementów multiPolygonu, które są od siebie odległe (nie jeden w drugim)
        let distantPolygonsArray = coordinates.split("?");

        for (let i = 0; i < distantPolygonsArray.length; i++) {
            coordinatesArray[i] = [];
        }

        for (let i = 0; i < distantPolygonsArray.length; i++) {
            //tablica elementów multiPolygonu które są jeden w drugim
            let insideCoordinatesArray = distantPolygonsArray[i].split("|");

            for (let j = 0; j < insideCoordinatesArray.length; j++) {
                coordinatesArray[i][j] = insideCoordinatesArray[j].split(" ").filter(coords => coords !== "").map(coord => fromLonLat(coord.split(",")));
            }
        }
        geo = new MultiPolygon(coordinatesArray);
    } else if(points.length <= 2 && type !== 'LineString') {
        geo = new Point(fromLonLat(points));
    } else {
        if(type === 'LineString') {
            coordinatesArray = points.filter(coords => coords !== "").map(coord => fromLonLat(coord.split(",")));
            geo = new LineString(coordinatesArray);
        } else {
            coordinatesArray = [points.filter(coords => coords !== "").map(coord => fromLonLat(coord.split(",")))];
            geo = new Polygon(coordinatesArray);
        }
    }

    return new Feature({
        geometry: geo,
        name,
        changed: false
    });
}

export const featureToGeoJSONObject = (feature) => {
    const format = new GeoJSON();
    return format.writeFeatureObject(feature, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857'});
}

export const coordinatesToGeoJSON = (coordinates, type = 'Polygon') => {
    if(type === 'Point') {
        return turf.point([
            parseFloat(coordinates.split(',')[0]),
            parseFloat(coordinates.split(',')[1]),
        ]);
    }

    let transformedCoordinates = coordinates
        ?.split(' ')
        .filter(str => str.includes(','))
        .map((latLong) => ([
            parseFloat(latLong.split(',')[0]) ?? 0,
            parseFloat(latLong.split(',')[1]) ?? 0,
        ]));

    if (type === 'Polygon') {
        // transformedCoordinates = closePolygon(transformedCoordinates);
        return turf.polygon([transformedCoordinates])
    } else if(type === 'LineString') {
        return turf.lineString(transformedCoordinates);
    }
}


export const geoJSONtoOLFeature = (geoJSON, transform = true) => {
    const format = new GeoJSON();

    const feature = format.readFeature(geoJSON);

    if(transform) {
        feature.getGeometry().transform('EPSG:4326', 'EPSG:3857');
    }

    return feature;
}

/**
 * Generate GeoJSON lineString collection that follow points path
 * @param points
 * @return {*[]}
 */
export const generateLinesBasedOnPoints = (points) => {
    const lines = [];
    for(let i = 0; i < points.features.length; i++) {
        let point = points.features[i];

        if(i !== points.features.length - 1) {
            let line = turf.lineString([
                point.geometry.coordinates,
                points.features[i + 1].geometry.coordinates
            ])

            lines.push(line);
        }
    }

    return lines;
}

/**
 * Create OL feature based on GeoJSON line, side and width
 * @param line
 * @param side
 * @param width
 * @param id
 * @return {Feature<Polygon>}
 */
export const createPolygonFeatureByLineAndWidth = (line, side, width, id = null) => {
    const left = side === 'LEFT';

    let bearing = null;

    // obliczanie nowych koordynat punktów linii po przesunięciu
    const coordinates = line.geometry.coordinates.map((coord, i, coords) => {
        const prevCoord = coords[i - 1] || coords[i];
        const nextCoord = coords[i + 1] || coords[i];
        const angle = turf.bearing(prevCoord, nextCoord) - 90; // kąt normalny

        if(left) {
            bearing = angle;
        } else {
            bearing = angle - 180;
        }

        const offset = turf.destination(coord, left ? width : -width, angle, {units: 'meters'}); // punkt przesunięcia
        return offset.geometry.coordinates;
    });

    const perpendicularLine = turf.lineString(coordinates); // linia o dokładnie zadanej długości

    // tworzenie poligonu wokół dwóch linii
    const polygon = turf.polygon([
        [
            ...turf.getCoords(line)
                .concat(turf.getCoords(perpendicularLine).reverse()),
            turf.getCoords(line)[0]
        ]
    ]);

    const feature = geoJSONtoOLFeature(polygon);
    feature.set('side', side);
    feature.set('width', width)
    feature.set('bearing', bearing);

    if(id === null) {
        id = nanoid();
    }

    feature.set('id', id);

    return feature;
}

/**
 * From RN app
 * @param line
 * @param side
 * @param width
 * @return {Feature<Polygon, Properties>}
 */
export const createPolygonByLineAndWidth = (line, side, width) => {
    const left = side === 'LEFT';

    // obliczanie nowych koordynat punktów linii po przesunięciu
    const coordinates = line.geometry.coordinates.map((coord, i, coords) => {
        const prevCoord = coords[i - 1] || coords[i];
        const nextCoord = coords[i + 1] || coords[i];
        const angle = turf.bearing(prevCoord, nextCoord) - 90; // kąt normalny

        const offset = turf.destination(coord, left ? width : -width, angle, {units: 'meters'}); // punkt przesunięcia
        return offset.geometry.coordinates;
    });

    const perpendicularLine = turf.lineString(coordinates); // linia o dokładnie zadanej długości

    // tworzenie poligonu wokół dwóch linii
    return turf.polygon([
        [
            ...turf.getCoords(line)
                .concat(turf.getCoords(perpendicularLine).reverse()),
            turf.getCoords(line)[0]
        ]
    ]);
}

/**
 * Generate points on line, keeping the distance
 * @param line
 * @param distance
 * @return {FeatureCollection<Point, Properties>|FeatureCollection<Geometry, Properties>}
 */
export const generatePointsOnLine = (line, distance) => {
    const length = turf.length(line, { units: 'meters' });
    const numberOfPoints = Math.ceil(length / distance);

    const points = [];

    for (let i = 0; i < numberOfPoints; i++) {
        const point = turf.along(line, i * distance, { units: 'meters' });
        points.push(point);
    }

    // Dodaj ostatni punkt, jeśli nie znajduje się już na linii
    const lastPoint = points[points.length - 1];
    if(typeof lastPoint == 'undefined') {
        return turf.featureCollection([
            turf.point(turf.getCoords(line)[0]),
            turf.point(turf.getCoords(line)[turf.getCoords(line).length - 1])
        ])
    }

    const lastPointDistance = turf.distance(lastPoint, turf.point(turf.getCoords(line)[turf.getCoords(line).length - 1]), { units: 'meters' });

    if (lastPointDistance > 2) {
        const endPoint = turf.point(turf.getCoords(line)[turf.getCoords(line).length - 1]);
        points.push(endPoint);
    }

    return turf.featureCollection(points);
}

export const generateLineParallelToAnotherLineOnPoint = (line, point) => {
    const distance = turf.pointToLineDistance(point, line, { units: 'meters' });
    return turf.lineOffset(line, distance, { units: 'meters' });
}

export const quickGeojsonPolygonUnion = (featureCollection) => {
    const coordinates = featureCollection.features.map(feature => turf.getCoords(feature))

    try {
        polygonClipping.union(...coordinates)
    } catch(err) {
        console.log(err)
    }

    return turf.multiPolygon(
        polygonClipping.union(...coordinates)
    );
}